/**
 * Branded Uint32Array types that distinguish triangle index spaces at compile time.
 *
 * `RenderTriangleIndices` index into per-corner render buffers such as the
 * arrays returned by `expandForRendering()`.
 *
 * `SharedTriangleIndices` index directly into shared-vertex position buffers.
 *
 * Mixing these two spaces was the root cause of multiple sketch/sculpt/modifier
 * bugs. The brands make those mismatches explicit at the boundaries where a
 * raw `Uint32Array` becomes semantically meaningful.
 */

declare const __renderTriangleIndicesBrand: unique symbol;
declare const __sharedTriangleIndicesBrand: unique symbol;

/** Triangle indices into expanded per-corner render arrays. */
export type RenderTriangleIndices = Uint32Array & {
  readonly [__renderTriangleIndicesBrand]: true;
};

/** Triangle indices into shared-vertex position arrays. */
export type SharedTriangleIndices = Uint32Array & {
  readonly [__sharedTriangleIndicesBrand]: true;
};

function assertTriangleIndexBuffer(label: string, buf: Uint32Array): void {
  if (import.meta.env.DEV && buf.length % 3 !== 0) {
    throw new Error(
      `${label}: buffer length ${buf.length} is not divisible by 3`,
    );
  }
}

export function asRenderTriangleIndices(
  buf: Uint32Array,
): RenderTriangleIndices {
  assertTriangleIndexBuffer("asRenderTriangleIndices", buf);
  return buf as RenderTriangleIndices;
}

export function asSharedTriangleIndices(
  buf: Uint32Array,
): SharedTriangleIndices {
  assertTriangleIndexBuffer("asSharedTriangleIndices", buf);
  return buf as SharedTriangleIndices;
}

export function cloneRenderTriangleIndices(
  indices: RenderTriangleIndices,
): RenderTriangleIndices {
  return indices.slice() as RenderTriangleIndices;
}

export function cloneSharedTriangleIndices(
  indices: SharedTriangleIndices,
): SharedTriangleIndices {
  return indices.slice() as SharedTriangleIndices;
}

export const EMPTY_RENDER_TRIANGLE_INDICES = asRenderTriangleIndices(
  new Uint32Array(0),
);
export const EMPTY_SHARED_TRIANGLE_INDICES = asSharedTriangleIndices(
  new Uint32Array(0),
);
